import React, { useContext, useEffect } from 'react';
import styles from './addLocations.module.scss';
import { CompanySetupEnum } from 'enums/CompanySetupEnums';
import clsx from 'clsx';
import Map from 'shared/components/map';
import InputField from 'shared/components/InputField';
import COSelect from 'shared/components/COSelect';
import { InitialValuesProp } from './constants';
import { categoryList } from 'views/CompanySetup/companySetupConstants';
import { convertToOptions } from 'shared/utils/convertOptions';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import useFilters from 'shared/hooks/useFilters';
import {
  isProcessingFacility,
  params,
  processingFacility,
} from 'constants/constants';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import MetadataService from 'services/ParentCompanySetup/metadata.service';

interface LocationFormFieldsProps {
  index: number;
  values: InitialValuesProp;
  setFieldValue: Function;
  searchPlaceByCountryProvince: (
    country: string,
    province: string,
    setFieldValue: Function,
    index: number
  ) => void;
  setAddressInState: (
    addressComponents: google.maps.GeocoderAddressComponent[],
    latlng: google.maps.LatLng | null,
    setFieldValue: Function,
    index: string
  ) => void;
  setOpenModalIndex: (index: number | null) => void;
  openModalIndex: number | null;
  fetchingAddress: boolean;
}

const LocationFormFields: React.FC<LocationFormFieldsProps> = ({
  index,
  values,
  setFieldValue,
  searchPlaceByCountryProvince,
  setAddressInState,
  setOpenModalIndex,
}) => {
  const { belongsMetaData, fetchBelongsMetaData } = MetadataService();
  const { getParams } = useFilters();
  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const { isProcessingFacility: isPrefill } = getParams([isProcessingFacility]);
  useEffect(() => {
    fetchBelongsMetaData();
    if (isPrefill) {
      setFieldValue(`locations[0].category`, processingFacility);
    }
  }, []);
  const handleClick = (index: number) => () => {
    setOpenModalIndex(index);
  };
  const handleBlur = (
    index: number,
    values: InitialValuesProp,
    setFieldValue: Function
  ) => {
    return () => {
      searchPlaceByCountryProvince(
        values.locations[index].country ?? '',
        values.locations[index].province ?? '',
        setFieldValue,
        index
      );
    };
  };
  const handleSetAddress = (
    addressComponents: google.maps.GeocoderAddressComponent[],
    latlng: google.maps.LatLng | null,
    setFieldValue: Function,
    index: number
  ) => {
    setAddressInState(addressComponents, latlng, setFieldValue, `${index}`);
  };
  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);

  const { locationName, locationCategory, locationAddress } =
    HelpDeskInputFieldEnum;

  return (
    <div className={styles.fieldContainer}>
      <div className={styles.inputNameContainer}>
        <InputField
          name={`locations[${index}].name`}
          placeholderstring="Enter The Name Of Facility Or Warehouse"
          label="Enter Your Locations"
          info
          infoClick={handleToolTipOpen(locationName)}
        />
      </div>
      <div className={styles.inputContainer}>
        <COSelect
          name={`locations[${index}].category`}
          placeholderstring="Location Category"
          label="Location Category"
          options={categoryList}
          disabled={isPrefill}
          info
          infoClick={handleToolTipOpen(locationCategory)}
        />
      </div>
      <div className={clsx(styles.inputContainer, styles.longContainer)}>
        <div className={styles.mapContainer}>
          <Map
            name={`locations[${index}].map`}
            setAddress={(addressComponents, latlng) =>
              handleSetAddress(addressComponents, latlng, setFieldValue, index)
            }
            latitude={values?.locations[index]?.latitude}
            longitude={values?.locations[index]?.longitude}
            info
            infoClick={handleToolTipOpen(locationAddress)}
          />
        </div>
      </div>
      <div className={styles.inputContainer}>
        <InputField
          name={`locations[${index}].street`}
          placeholderstring="Street, Locality"
          label="Street"
        />
      </div>
      <div className={styles.inputContainer}>
        <InputField
          name={`locations[${index}].city`}
          placeholderstring="City"
          label="City"
        />
      </div>
      <div className={styles.inputContainer}>
        <InputField
          name={`locations[${index}].province`}
          placeholderstring="Province"
          label="Province"
          onBlur={handleBlur(index, values, setFieldValue)}
        />
      </div>
      <div className={styles.inputContainer}>
        <InputField
          name={`locations[${index}].postalCode`}
          placeholderstring="Postal Code"
          label="Postal Code"
        />
      </div>
      <div className={styles.inputContainer}>
        <COSelect
          name={`locations[${index}].belongs`}
          placeholderstring="Belongs To"
          label="Belongs To"
          options={convertToOptions(belongsMetaData)}
        />
      </div>
      <div className={styles.inputContainer}>
        <div className={styles.orgBoundary}>
          <div onClick={handleClick(index)}>
            {CompanySetupEnum.ORG_BOUNDARY} <Arrow className={styles.arrowIcon} fontSize={44} />
          </div>

          <div
            className={clsx(styles.status, {
              [styles.statusComplete]: values.locations?.[index]?.years?.length,
              [styles.statusIncomplete]:
                !values.locations?.[index]?.years?.length,
            })}>
            {values.locations?.[index]?.years?.length
              ? CompanySetupEnum.COMPLETED
              : CompanySetupEnum.INCOMPLETED}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationFormFields;
